import { useEffect, useRef, useState } from "react";
import { useBlocker } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  SelectInput,
  Tabs,
  Typography,
  useToaster
} from "@dealer-center/core-components";
import { SaveAndCancel } from "@dealer-center/core-custom-components";
import { Locale } from "@dealer-center/core-labels";
import type {
  FeeManagerConfig,
  FeeManagerTenantSchema,
  PriceAfterFeesAttribute
} from "@dealer-center/core-feemanager-interface";
import {
  coerceSubTogglesToMaster,
  getConfigDefaults,
  normalizeFeeManagerConfig
} from "@dealer-center/core-feemanager-interface";
import {
  useFees,
  useGetConfig,
  useSaveConfig,
  useSaveFees
} from "@dealer-center/core-feemanager-fetch";
import { migrateAllFees, useFeeState } from "./hooks/useFeeState";
import { FeeRow } from "./components/FeeRow";
import { FeeRowSkeleton } from "./components/FeeRowSkeleton";
import { TotalFeesDisplay } from "./components/TotalFeesDisplay";
import { AddFeeModal } from "./components/AddFeeModal";
import { ConfigurationModal } from "./components/ConfigurationModal";
import { UnsavedChangesModal } from "./components/UnsavedChangesModal";
import {
  ButtonGroup,
  HeaderCell,
  PageContainer,
  PageHeader,
  TableActionsRow,
  TableBody,
  TableContainer,
  TableHeader,
  TabsContainer
} from "./styles/FeeManager.styles";
import LABELS from "./labels.json";

Locale.setLabelsOnce(LABELS); // module-level — runs once at import time

const priceAfterFeesOptions = (): {
  label: string;
  value: PriceAfterFeesAttribute;
}[] => [
  { label: String(Locale.getText("ASKING_PRICE")), value: "askingPrice" },
  { label: String(Locale.getText("INTERNET_PRICE")), value: "internetPrice" },
  { label: String(Locale.getText("INVOICE_PRICE")), value: "invoicePrice" },
  { label: String(Locale.getText("MSRP")), value: "msrp" },
  { label: String(Locale.getText("RETAIL_VALUE")), value: "retailValue" },
  { label: String(Locale.getText("SALE_PRICE")), value: "salePrice" },
  { label: String(Locale.getText("WHOLESALE_PRICE")), value: "wholesalePrice" }
];

const getDefaultSchema = (): FeeManagerTenantSchema => ({
  NEW_applyOnInventoryItemSave: {
    type: "boolean",
    defaultValue: false,
    label: String(Locale.getText("APPLY_ON_INVENTORY_ITEM_SAVE"))
  },
  NEW_saveTotalFees: {
    type: "boolean",
    defaultValue: false,
    label: String(Locale.getText("EMIT_TOTAL_FEES"))
  },
  NEW_saveItemizedFees: {
    type: "boolean",
    defaultValue: false,
    label: String(Locale.getText("EMIT_ITEMIZED_FEES"))
  },
  NEW_savePriceAfterFees: {
    type: "boolean",
    defaultValue: false,
    label: String(Locale.getText("EMIT_PRICE_AFTER_FEES"))
  },
  NEW_priceAfterFeesInputAttribute: {
    type: "select",
    defaultValue: "internetPrice",
    label: String(Locale.getText("PRICE_AFTER_FEES_INPUT_ATTRIBUTE")),
    options: priceAfterFeesOptions(),
    disabled: (config: FeeManagerConfig) => !config.NEW_applyOnInventoryItemSave
  },
  USED_applyOnInventoryItemSave: {
    type: "boolean",
    defaultValue: false,
    label: String(Locale.getText("APPLY_ON_INVENTORY_ITEM_SAVE"))
  },
  USED_saveTotalFees: {
    type: "boolean",
    defaultValue: false,
    label: String(Locale.getText("EMIT_TOTAL_FEES"))
  },
  USED_saveItemizedFees: {
    type: "boolean",
    defaultValue: false,
    label: String(Locale.getText("EMIT_ITEMIZED_FEES"))
  },
  USED_savePriceAfterFees: {
    type: "boolean",
    defaultValue: false,
    label: String(Locale.getText("EMIT_PRICE_AFTER_FEES"))
  },
  USED_priceAfterFeesInputAttribute: {
    type: "select",
    defaultValue: "internetPrice",
    label: String(Locale.getText("PRICE_AFTER_FEES_INPUT_ATTRIBUTE")),
    options: priceAfterFeesOptions(),
    disabled: (config: FeeManagerConfig) =>
      !config.USED_applyOnInventoryItemSave
  }
});

export interface FeeManagerProps {
  tenantType: string;
  tenantId: string;
  tenantUser: string;
  hasAccess: boolean;
  basePath: string;
  configSchema?: FeeManagerTenantSchema;
  clientId?: string;
}

export const FeeManager = ({
  tenantType,
  tenantId,
  tenantUser,
  hasAccess,
  basePath,
  configSchema,
  clientId
}: FeeManagerProps) => {
  const schema = configSchema ?? getDefaultSchema();

  const { toastError } = useToaster();
  const [activeTab, setActiveTab] = useState<"NEW" | "USED">("NEW");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [editingFeeId, setEditingFeeId] = useState<string | null>(null);

  const {
    data: feesData,
    isError: isFeesError,
    isLoading: isFeesLoading
  } = useFees({ tenantType, tenantId, disabled: !hasAccess, basePath });

  const { data: configData, isError: isConfigError } = useGetConfig({
    tenantType,
    tenantId,
    disabled: !hasAccess,
    basePath
  });

  const feeState = useFeeState();

  useEffect(() => {
    if (feesData?.fees) {
      const hasAllFees = feesData.fees.some(f => (f.type as string) === "ALL");
      feeState.setServerAndDraftFees(feesData.fees);
      if (hasAllFees) {
        const migratedFees = migrateAllFees(feesData.fees);
        saveFeesMutation.mutate(
          { fees: migratedFees, tenantUser },
          {
            onSuccess: data => {
              if (data.success) {
                feeState.setServerAndDraftFees(data.fees);
              } else {
                feeState.setServerAndDraftFees(migratedFees);
              }
            },
            onError: () => {
              toastError(String(Locale.getText("FEEMANAGER_ERROR_SAVE_TITLE")));
              feeState.setServerAndDraftFees(migratedFees);
            }
          }
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feesData]);

  useEffect(() => {
    if (isFeesError) {
      toastError(String(Locale.getText("FEEMANAGER_ERROR_LOAD_FEES")));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFeesError]);

  useEffect(() => {
    if (isConfigError) {
      toastError(String(Locale.getText("FEEMANAGER_ERROR_LOAD_CONFIG")));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConfigError]);

  const [configDraft, setConfigDraft] = useState<FeeManagerConfig>(
    getConfigDefaults(schema)
  );
  const [normalizedConfigData, setNormalizedConfigData] = useState<
    FeeManagerConfig | undefined
  >(undefined);
  const [isConfigUnfulfilled, setIsConfigUnfulfilled] = useState(false);

  // Refs to avoid stale closure issues inside the configData effect without
  // adding isConfigOpen to its dependency array (which would cause unnecessary re-runs).
  const isConfigOpenRef = useRef(isConfigOpen);
  isConfigOpenRef.current = isConfigOpen;
  // Tracks whether the initial config load has been processed; subsequent cache
  // updates (e.g. triggered by a save's setQueryData) should not re-set the flag.
  const isInitialConfigLoadRef = useRef(true);

  useEffect(() => {
    if (configData) {
      const { config, isNormalized } = normalizeFeeManagerConfig(
        configData as Record<string, unknown>
      );
      setNormalizedConfigData(config);
      // Skip overwriting an in-progress popup edit when the cache refreshes.
      if (!isConfigOpenRef.current) {
        setConfigDraft(config);
      }
      if (isNormalized && isInitialConfigLoadRef.current) {
        setIsConfigUnfulfilled(true);
      }
      isInitialConfigLoadRef.current = false;
    }
  }, [configData]);

  const blocker = useBlocker(feeState.isDirty);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (feeState.isDirty) {
        e.preventDefault();
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [feeState.isDirty]);

  const saveFeesMutation = useSaveFees({
    tenantType,
    tenantId,
    basePath,
    clientId
  });
  const saveConfigMutation = useSaveConfig({
    tenantType,
    tenantId,
    basePath,
    clientId
  });

  const isSaving = saveFeesMutation.isPending;
  const isFeeEditingDisabled = isSaving;

  const visibleFees = (
    feeState.showRemoved
      ? feeState.draftFees.filter(f => f.removed)
      : feeState.draftFees.filter(f => !f.removed)
  ).filter(f => f.type === activeTab);

  const editingFee =
    editingFeeId !== null
      ? (feeState.draftFees.find(f => f.id === editingFeeId) ?? null)
      : null;

  const tabs = [
    {
      active: activeTab === "NEW",
      disabled: isSaving,
      label: String(Locale.getText("FEEMANAGER_NEW_TAB")),
      value: 0
    },
    {
      active: activeTab === "USED",
      disabled: isSaving,
      label: String(Locale.getText("FEEMANAGER_PREOWNED_TAB")),
      value: 1
    }
  ];

  const handleSave = () => {
    saveFeesMutation.mutate(
      { fees: feeState.draftFees, tenantUser },
      {
        onSuccess: data => {
          if (data.success) {
            feeState.setServerAndDraftFees(data.fees);
          } else if (data.error === "CONFLICT") {
            toastError(String(Locale.getText("FEEMANAGER_CONFLICT_ERROR")));
          }
        },
        onError: () => {
          toastError(String(Locale.getText("FEEMANAGER_ERROR_SAVE_TITLE")));
        }
      }
    );

    if (isConfigUnfulfilled) {
      const { config: configToSave } = coerceSubTogglesToMaster(configDraft);
      saveConfigMutation.mutate(
        { config: configToSave, tenantUser },
        {
          onSuccess: data => {
            if (data.success) {
              setNormalizedConfigData(data.config);
              setIsConfigUnfulfilled(false);
            }
          }
        }
      );
    }
  };

  const handleTabActivate = (index: number) => {
    setActiveTab(index === 0 ? "NEW" : "USED");
    setEditingFeeId(null);
  };

  const handleAdd = (data: {
    name: string;
    amount: number | null;
    type: "NEW" | "USED";
  }) => {
    feeState.addDraftFee({
      amount: data.amount,
      id: crypto.randomUUID(),
      name: data.name,
      removed: false,
      type: data.type
    });
    setIsAddModalOpen(false);
  };

  const handleOpenEdit = (feeId: string) => setEditingFeeId(feeId);
  const handleCloseEdit = () => setEditingFeeId(null);

  const handleUpdate = (
    updates: Partial<{
      name: string;
      amount: number | null;
      type: "NEW" | "USED";
    }>
  ) => {
    if (editingFeeId) {
      feeState.updateDraftFee(editingFeeId, updates);
    }
  };

  const handleDeleteFee = (feeId: string) => feeState.softDelete([feeId]);
  const handleRestoreFee = (feeId: string) => feeState.restore([feeId]);
  const handleOpenConfig = () => setIsConfigOpen(true);

  const handleCloseConfig = () => {
    setIsConfigOpen(false);
    setConfigDraft(normalizedConfigData ?? getConfigDefaults(schema));
  };

  const handleConfigChange = (patch: Partial<FeeManagerConfig>) => {
    setConfigDraft(prev => ({ ...prev, ...patch }));
  };

  const handleSaveConfig = () => {
    const { config: configToSave } = coerceSubTogglesToMaster(configDraft);
    saveConfigMutation.mutate(
      { config: configToSave, tenantUser },
      {
        onSuccess: data => {
          if (data.success) {
            setNormalizedConfigData(data.config);
            setConfigDraft(data.config);
            setIsConfigUnfulfilled(false);
            setIsConfigOpen(false);
          } else if (data.error === "CONFLICT") {
            toastError(String(Locale.getText("FEEMANAGER_CONFLICT_ERROR")));
          }
        },
        onError: () => {
          toastError(
            String(Locale.getText("FEEMANAGER_ERROR_SAVE_CONFIG_TITLE"))
          );
        }
      }
    );
  };

  if (!hasAccess) {
    return (
      <Alert role="alert" sx={{ marginBottom: "16px" }} type="error">
        {String(Locale.getText("NO_ACCESS"))}
      </Alert>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <Typography variant={"h1"}>
          {String(Locale.getText("FEEMANAGER_TITLE"))}
        </Typography>
        <Button
          analyticsConfiguration={{
            eventName: "Fee Manager Configuration",
            eventProperties: undefined
          }}
          buttonStyle="secondary"
          disabled={configData === undefined}
          onClick={handleOpenConfig}
        >
          {String(Locale.getText("CONFIGURATION"))}
        </Button>
      </PageHeader>

      <TabsContainer disabled={isSaving}>
        <Tabs id="fee-tabs" tabs={tabs} onActivate={handleTabActivate} />
      </TabsContainer>

      <TableActionsRow>
        <SelectInput
          data-testid="fee-view-filter"
          displayDeselectOption={false}
          id="fee-view-filter"
          name="fee-view-filter"
          options={[
            {
              value: "active",
              label: String(Locale.getText("FEEMANAGER_SHOW_ACTIVE"))
            },
            {
              value: "deleted",
              label: String(Locale.getText("FEEMANAGER_SHOW_DELETED"))
            }
          ]}
          value={feeState.showRemoved ? "deleted" : "active"}
          onChange={e => {
            const shouldShowRemoved = e.target.value === "deleted";
            if (shouldShowRemoved !== feeState.showRemoved) {
              feeState.toggleShowRemoved();
            }
          }}
        />
        <ButtonGroup>
          <Button
            analyticsConfiguration={{
              eventName: "Fee Manager Add Fee",
              eventProperties: undefined
            }}
            buttonStyle="secondary"
            disabled={
              isFeeEditingDisabled ||
              configData === undefined ||
              feesData === undefined
            }
            onClick={() => setIsAddModalOpen(true)}
          >
            {String(Locale.getText("FEEMANAGER_ADDFEE"))}
          </Button>
        </ButtonGroup>
      </TableActionsRow>

      <TableContainer>
        <TableHeader disabled={isSaving}>
          <HeaderCell>{String(Locale.getText("FEEMANAGER_NAME"))}</HeaderCell>
          <HeaderCell $shiftLeft>
            {String(Locale.getText("FEEMANAGER_AMOUNT"))}
          </HeaderCell>
          <HeaderCell style={{ paddingLeft: "32px" }}>
            {String(Locale.getText("ACTIONS"))}
          </HeaderCell>
        </TableHeader>

        {isFeesLoading ? (
          <TableBody disabled>
            <FeeRowSkeleton count={5} />
          </TableBody>
        ) : (
          <TableBody>
            {visibleFees.map(fee => (
              <FeeRow
                disabled={isFeeEditingDisabled || !fee.id}
                fee={fee}
                key={fee.id ?? `migrated-${fee.type}-${fee.name}`}
                locale={Locale.getLocale()}
                onDelete={handleDeleteFee}
                onOpenEdit={handleOpenEdit}
                onRestore={handleRestoreFee}
              />
            ))}
          </TableBody>
        )}

        {!feeState.showRemoved && (
          <TotalFeesDisplay
            disabled={false}
            locale={Locale.getLocale()}
            total={
              activeTab === "NEW"
                ? feeState.totalAmountNew
                : feeState.totalAmountUsed
            }
            vehicleType={activeTab}
          />
        )}
      </TableContainer>

      <AddFeeModal
        activeTab={activeTab}
        isOpen={isAddModalOpen}
        mode="add"
        onAdd={handleAdd}
        onCancel={() => setIsAddModalOpen(false)}
      />

      {editingFee !== null && (
        <AddFeeModal
          isOpen
          fee={editingFee}
          mode="edit"
          onClose={handleCloseEdit}
          onUpdate={handleUpdate}
        />
      )}

      <ConfigurationModal
        config={configDraft}
        isConfigDirty={
          isConfigUnfulfilled ||
          JSON.stringify(configDraft) !==
            JSON.stringify(normalizedConfigData ?? getConfigDefaults(schema))
        }
        isOpen={isConfigOpen}
        isSaving={saveConfigMutation.isPending}
        schema={schema}
        onChange={handleConfigChange}
        onClose={handleCloseConfig}
        onSave={handleSaveConfig}
      />

      <UnsavedChangesModal
        isOpen={blocker.state === "blocked"}
        onLeave={() => blocker.proceed?.()}
        onStay={() => blocker.reset?.()}
      />

      <Box sx={{ marginTop: "16px" }}>
        <SaveAndCancel
          analyticsEventName="Fee Manager"
          cancelButtonText={String(Locale.getText("FEEMANAGER_CANCEL"))}
          isDisabled={{
            cancel: !feeState.isDirty,
            save: !feeState.isDirty
          }}
          isSaving={isSaving}
          saveButtonText={String(Locale.getText("FEEMANAGER_SAVE"))}
          savingText={String(Locale.getText("FEEMANAGER_SAVING"))}
          onCancel={feeState.handleCancel}
          onSave={handleSave}
        />
      </Box>
    </PageContainer>
  );
};
